import axios, { type InternalAxiosRequestConfig } from "axios";
import { toast } from "sonner";

import {
  expireAuthSession,
  getAuthSessionVersion,
  getStoredAuthToken,
  isAuthSessionCurrent,
} from "@/shared/auth/session";
import i18n from "@/shared/config/i18n/client";
import { getFingerprint } from "@/shared/lib/fingerprint";
import { logger } from "@/shared/lib/logger";

import { apiBaseUrl } from "./config";
import { getOrCreateIdempotencyKey } from "./utils";

export const baseUrl = apiBaseUrl;

if (process.env.NODE_ENV !== "production") {
  logger.warn(`API client configured for ${baseUrl}`);
}

export const http = axios.create({
  baseURL: baseUrl,
  withCredentials: true,
  headers: {
    Accept: "application/json",
  },
});

const requestSessions = new WeakMap<InternalAxiosRequestConfig, { version: string; canExpire: boolean }>();

http.interceptors.request.use(
  async (config) => {
    if (typeof window !== "undefined") {
      const version = getAuthSessionVersion();
      const token = getStoredAuthToken();
      const authorization = config.headers.Authorization;
      const credentialAttempt = /\/app\/auth\/(?:login|register|forgot-password)(?:\/|\?|$)/.test(config.url ?? "");
      requestSessions.set(config, {
        version,
        canExpire: !credentialAttempt && (!authorization || authorization === `Bearer ${token}`),
      });
      const method = config.method?.toUpperCase();
      if (method && ["POST", "PUT", "DELETE"].includes(method)) {
        config.headers["Idempotency-Key"] = getOrCreateIdempotencyKey();
      }

      // Attach Authorization: Bearer as a fallback when the HttpOnly auth cookie
      // can't ride along (iOS Safari/Chrome and any browser that blocks
      // third-party cookies). The cookie path still works in browsers that
      // allow it; the backend accepts either.
      if (!config.headers.Authorization) {
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }

      try {
        const visitorId = await getFingerprint();
        config.headers["X-Fingerprint"] = visitorId;
      } catch {
        // Fingerprint load failed — proceed without it
      }
      // A queued write must never acquire the next account's browser cookie.
      if (!isAuthSessionCurrent(version)) throw new axios.CanceledError("Authentication changed before dispatch");
    }

    return config;
  },
  (error) => Promise.reject(error),
);

function formatWaitTime(seconds: number): string {
  if (seconds >= 60) {
    const minutes = Math.ceil(seconds / 60);
    return i18n.t("common.rateLimit.minutes", { count: minutes });
  }
  return i18n.t("common.rateLimit.seconds", { count: seconds });
}

http.interceptors.response.use(undefined, (error) => {
  if (axios.isAxiosError(error) && error.response?.status === 429) {
    const retryAfter = Number.parseInt(error.response.headers["retry-after"] || "", 10);
    const seconds = Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : 60;

    toast.warning(i18n.t("common.rateLimit.message", { time: formatWaitTime(seconds) }), {
      duration: Math.min(seconds * 1000, 10_000),
    });
  }

  if (axios.isAxiosError(error) && error.response?.status === 401) {
    const requestSession = error.config && requestSessions.get(error.config);
    if (requestSession?.canExpire) expireAuthSession(requestSession.version);
  }

  return Promise.reject(error);
});
